import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { getMeetings } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Loader2, 
  ArrowRight,
  CalendarClock,
  Clock,
  MapPin,
  Link as LinkIcon,
  Eye,
  EyeOff,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  'Planificada': 'bg-blue-100 text-blue-700 border-blue-200',
  'Convocada': 'bg-amber-100 text-amber-700 border-amber-200',
  'Realizada': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Cancelada': 'bg-slate-100 text-slate-500 border-slate-200',
};

const MeetingsPage = () => {
  const { canManageTasks, isSuperadmin, user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadMeetings();
  }, [filterStatus]);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const response = await getMeetings(params);
      setMeetings(response.data);
    } catch (error) {
      console.error('Error loading meetings:', error);
      toast.error('Error al cargar las reuniones');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterStatus('all');
  };

  const hasFilters = filterStatus && filterStatus !== 'all';

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="meetings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
              Reuniones
            </h1>
            <p className="text-slate-500 mt-1">{meetings.length} reuniones</p>
          </div>
          {canManageTasks() && (
            <Link to="/meetings/new">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-meeting-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Reunion
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-6 bg-white border border-stone-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px] h-11 border-stone-200" data-testid="filter-meeting-status">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Planificada">Planificada</SelectItem>
                  <SelectItem value="Convocada">Convocada</SelectItem>
                  <SelectItem value="Realizada">Realizada</SelectItem>
                  <SelectItem value="Cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && (
                <Button variant="ghost" onClick={clearFilters} className="h-11 text-slate-600">
                  <X className="w-4 h-4 mr-1" />
                  Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Meetings List */}
        <Card className="bg-white border border-stone-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : meetings.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <CalendarClock className="w-12 h-12 mb-3 text-slate-300" />
                <p className="text-lg font-medium">No hay reuniones</p>
                <p className="text-sm">Crea una nueva reunion para empezar</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {meetings.map((meeting) => (
                  <Link
                    key={meeting.id}
                    to={`/meetings/${meeting.id}`}
                    className="block hover:bg-stone-50 transition-colors"
                    data-testid={`meeting-row-${meeting.id}`}
                  >
                    <div className="p-5 flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                        <CalendarClock className="w-6 h-6 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-slate-900 truncate">{meeting.title}</h3>
                          <Badge className={`${statusColors[meeting.status] || 'bg-slate-100 text-slate-500'} text-[10px] font-bold uppercase border`}>
                            {meeting.status}
                          </Badge>
                          {(isSuperadmin() || user?.role === 'ugib') && (
                            meeting.visible_to_departamentos 
                              ? <Eye className="w-4 h-4 text-emerald-500" title="Visible" />
                              : <EyeOff className="w-4 h-4 text-amber-500" title="Oculta" />
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <CalendarClock className="w-3.5 h-3.5" />
                            {meeting.date}
                          </span>
                          {meeting.time && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {meeting.time}
                            </span>
                          )}
                          {meeting.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {meeting.location}
                            </span>
                          )}
                          {meeting.meeting_link && (
                            <span className="flex items-center gap-1">
                              <LinkIcon className="w-3.5 h-3.5" />
                              Enlace
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                          <span>{meeting.tasks_details?.length || 0} temas</span>
                          <span>{meeting.agents_details?.length || 0} agentes</span>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-300 shrink-0 mt-1" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MeetingsPage;
